import { useEffect, useRef, useState } from 'react';
import {
  openGateStateStream,
  type GateConnectionState,
  type GateStreamHandle,
} from '../lib/gateStateStream';
import type { GateStateSnapshot } from '../types/systemHealth.types';

/**
 * Subscribe to the live concurrency-gate SSE stream (86d3peur0 Phase C) and expose the latest snapshot +
 * connection state. **Visibility-gated:** the stream is only open while the tab is visible — when the tab is
 * hidden we close it, which stops the server's per-connection push loop (the guardrail that keeps this ~free
 * on the single box). Reopens on return. One connection per mount; closed on unmount.
 */
export function useGateStateStream(): {
  snapshot: GateStateSnapshot | null;
  connectionState: GateConnectionState;
} {
  const [snapshot, setSnapshot] = useState<GateStateSnapshot | null>(null);
  const [connectionState, setConnectionState] = useState<GateConnectionState>('connecting');
  const handleRef = useRef<GateStreamHandle | null>(null);

  useEffect(() => {
    let mounted = true;

    const open = () => {
      if (handleRef.current) return;
      handleRef.current = openGateStateStream({
        onSnapshot: (s) => {
          if (mounted) setSnapshot(s);
        },
        onConnectionStateChange: (c) => {
          if (mounted) setConnectionState(c);
        },
      });
    };

    const close = () => {
      handleRef.current?.close();
      handleRef.current = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') open();
      else close();
    };

    if (document.visibilityState === 'visible') open();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, []);

  return { snapshot, connectionState };
}
